import { useEffect, useState } from "react";
import { Download, Share, Menu, AlertCircle, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export function InstallApp() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isIosNotSafari = isIos && (/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|YaBrowser/i.test(ua));
  const [tab, setTab] = useState(isIos ? "ios" : "android");

  const [deferred, setDeferred] = useState(null);
  const [showHelp, setShowHelp] = useState(() => isIosNotSafari ||
    (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("install") === "1"));
  const [installed, setInstalled] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      toast.success("Link copiato! Aprilo in Safari");
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error("Copia manualmente l'indirizzo dalla barra del browser");
    }
  };

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone) { setInstalled(true); return; }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const click = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    } else {
      setShowHelp((v) => !v);
    }
  };

  return (
    <div data-testid="install-app" className="kc-card p-4">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-primary/12 grid place-items-center shrink-0">
          <Download className="w-5 h-5 text-primary" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">Scarica l'app ADTRAINING</p>
          <p className="text-xs text-muted-foreground">Installala sul telefono: tutto schermo, come un'app nativa.</p>
        </div>
        <button data-testid="install-app-button" onClick={click} className="kc-btn-primary !px-4 !py-2 text-sm shrink-0">
          Installa
        </button>
      </div>
      {showHelp && (
        <div className="mt-3 pt-3 border-t border-border text-sm" data-testid="install-app-help">
          {isIosNotSafari && (
            <div className="kc-card-dark p-4 mb-3" data-testid="install-ios-chrome-warning">
              <p className="flex items-start gap-2 font-semibold mb-3">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-accent" />
                Su iPhone l'installazione funziona solo da Safari. Apple non permette il passaggio automatico: copia il link e incollalo in Safari.
              </p>
              <button data-testid="install-copy-link" onClick={copyLink}
                className="kc-btn-gold w-full inline-flex items-center justify-center gap-2">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Link copiato!" : "1. Copia il link dell'app"}
              </button>
              <p className="text-xs opacity-70 mt-2">2. Apri <strong>Safari</strong> · 3. Incolla il link nella barra in alto · 4. Segui i passaggi iOS qui sotto</p>
            </div>
          )}
          <div className="flex rounded-lg border border-border overflow-hidden mb-3" data-testid="install-os-tabs">
            <button data-testid="install-tab-ios" onClick={() => setTab("ios")}
              className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-wider ${tab === "ios" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              iPhone / iOS
            </button>
            <button data-testid="install-tab-android" onClick={() => setTab("android")}
              className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-wider ${tab === "android" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              Android
            </button>
          </div>
          {tab === "ios" ? (
            <div className="space-y-1.5 text-muted-foreground" data-testid="install-help-ios">
              <p>1. Apri <strong>adtrainingstudio.it</strong> in <strong>Safari</strong></p>
              <p className="flex items-center gap-2">2. Tocca <Share className="w-4 h-4 text-primary shrink-0" /> <strong>Condividi</strong> (icona in basso al centro)</p>
              <p>3. Scorri e tocca <strong>«Aggiungi a schermata Home»</strong></p>
              <p>4. Conferma con <strong>Aggiungi</strong>: trovi l'icona ADTRAINING sulla Home</p>
            </div>
          ) : (
            <div className="space-y-1.5 text-muted-foreground" data-testid="install-help-android">
              <p>1. Apri <strong>adtrainingstudio.it</strong> in <strong>Chrome</strong></p>
              <p className="flex items-center gap-2">2. Tocca il menu <Menu className="w-4 h-4 text-primary shrink-0" /> <strong>⋮</strong> in alto a destra</p>
              <p>3. Tocca <strong>«Installa app»</strong> o <strong>«Aggiungi a schermata Home»</strong></p>
              <p>4. Conferma: trovi l'icona ADTRAINING sulla Home</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
